import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useLocation } from "wouter";
import {
  Building2, IndianRupee, Bell, ChevronRight,
  MapPin, Target, Clock, ArrowRight, Sparkles,
  Loader2, Search, Settings2, LogOut as ExitIcon, BadgeCheck,
  CheckCircle2, AlertCircle, MessageCircle, RotateCcw, Inbox,
  Phone, CalendarPlus, ExternalLink,
} from "lucide-react";
import InvestorPortalDisclaimer from "@/components/investor-portal-disclaimer";
import { useActiveCohort, withCohort } from "@/lib/cohort-context";
import InvestorCreditSummary from "@/components/investor-credit-summary";
import { ResearchDashboardFeature } from "@/pages/investor/investor-research-pipeline";

const BASE = import.meta.env.BASE_URL;

interface LatestSignal {
  kind: "founder_update" | "document" | "kpi" | "intel" | "system";
  occurredAt: string;
  title: string;
}

interface PortfolioCompany {
  company: {
    id: number;
    name: string;
    description: string | null;
    sector: string | null;
    stage: string | null;
    logoUrl: string | null;
    location: string | null;
  };
  amountInvested: number | null;
  entryDate: string | null;
  accessLevel: string;
  latestUpdateDate: string | null;
  latestSignal: LatestSignal | null;
}

const SIGNAL_ICONS: Record<LatestSignal["kind"], string> = {
  founder_update: "📝",
  document: "📄",
  kpi: "📊",
  intel: "⚠",
  system: "•",
};

function formatSignalAge(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "just now";
  const day = 24 * 60 * 60 * 1000;
  const d = Math.floor(ms / day);
  if (d === 0) return "today";
  if (d === 1) return "yesterday";
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

interface RofrAlert {
  id: number;
  companyId: number;
  companyName: string;
  roundName: string;
  roundSize: string | null;
  valuation: string | null;
  leadInvestor: string | null;
  deadline: string | null;
  myResponse: string | null;
}

interface CompanySignal {
  companyId: number;
  openTasksMine: number;
  lastBankerActionAt: string | null;
  lastUpdateAt: string | null;
  daysSinceLastUpdate: number | null;
}

interface AwaitingItem {
  id: number;
  title: string;
  companyId: number | null;
  companyName: string | null;
  closedAt: string | null;
  resolutionNote: string | null;
  reopenCount: number | null;
}

interface DismissedItem {
  id: number;
  title: string;
  companyId: number | null;
  companyName: string | null;
  dismissedAt: string | null;
  dismissReason: string | null;
}

interface UpcomingCall {
  id: number;
  title: string;
  companyId: number | null;
  companyName: string | null;
  meetingLink: string | null;
  scheduledFor: string | null;
  meetingNote: string | null;
}

interface Signals {
  since: string;
  sinceLastVisit: { newResolutions: number; newUpdates: number; newRofrs: number; unreadNotifications: number };
  awaitingConfirmation: AwaitingItem[];
  recentDismissals: DismissedItem[];
  upcomingCalls?: UpcomingCall[];
  companySignals: CompanySignal[];
  kavyaPrompts: { label: string; prompt: string }[];
}

// Build a single-event .ics file for the investor's calendar. Generated on
// the client because the data is small, the link can be a data: URL with no
// server round-trip, and we can update the format without a redeploy.
//
// Times are emitted as floating local times in Asia/Kolkata via TZID,
// accompanied by a VTIMEZONE block. The previous version emitted DTSTART in
// UTC (Z suffix) without VTIMEZONE — that's RFC-legal but Apple Calendar
// regularly mis-converts it to the user's local clock as if it were already
// local, so a 6 PM IST call would show up at midnight on a US laptop. Pinning
// to TZID:Asia/Kolkata + a VTIMEZONE block makes every major client agree.
function buildIcs(call: UpcomingCall): string {
  if (!call.scheduledFor) return "";
  const dt = new Date(call.scheduledFor);
  const end = new Date(dt.getTime() + 30 * 60 * 1000); // default 30-min slot

  // DTSTAMP must be in UTC per RFC 5545 (it's a creation timestamp, not the
  // event time). Local-floating times use the no-Z form below.
  const fmtUtc = (d: Date) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  // Format a JS Date as a wall-clock IST string YYYYMMDDTHHMMSS (no Z).
  // We compute IST components by shifting the UTC date by +5:30 and reading
  // the resulting UTC fields — this avoids relying on the host's timezone.
  const fmtIst = (d: Date) => {
    const shifted = new Date(d.getTime() + 5.5 * 60 * 60 * 1000);
    const y = shifted.getUTCFullYear().toString().padStart(4, "0");
    const m = (shifted.getUTCMonth() + 1).toString().padStart(2, "0");
    const day = shifted.getUTCDate().toString().padStart(2, "0");
    const hh = shifted.getUTCHours().toString().padStart(2, "0");
    const mm = shifted.getUTCMinutes().toString().padStart(2, "0");
    const ss = shifted.getUTCSeconds().toString().padStart(2, "0");
    return `${y}${m}${day}T${hh}${mm}${ss}`;
  };
  const escapeIcs = (s: string) => s.replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/,/g, "\\,").replace(/;/g, "\\;");
  const summary = `Favcy Select call${call.companyName ? ` — ${call.companyName}` : ""}: ${call.title}`;
  const descParts = [];
  if (call.meetingNote) descParts.push(call.meetingNote);
  if (call.meetingLink) descParts.push(`Join: ${call.meetingLink}`);
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Favcy Select//EN",
    "CALSCALE:GREGORIAN",
    // Asia/Kolkata is +05:30 year-round (no DST since 1945) so a single
    // STANDARD block with no recurrence is correct and stable.
    "BEGIN:VTIMEZONE",
    "TZID:Asia/Kolkata",
    "BEGIN:STANDARD",
    "DTSTART:19700101T000000",
    "TZOFFSETFROM:+0530",
    "TZOFFSETTO:+0530",
    "TZNAME:IST",
    "END:STANDARD",
    "END:VTIMEZONE",
    "BEGIN:VEVENT",
    `UID:favcy-task-${call.id}@favcy-select`,
    `DTSTAMP:${fmtUtc(new Date())}Z`,
    `DTSTART;TZID=Asia/Kolkata:${fmtIst(dt)}`,
    `DTEND;TZID=Asia/Kolkata:${fmtIst(end)}`,
    `SUMMARY:${escapeIcs(summary)}`,
    descParts.length ? `DESCRIPTION:${escapeIcs(descParts.join("\n"))}` : "",
    call.meetingLink ? `URL:${escapeIcs(call.meetingLink)}` : "",
    "END:VEVENT",
    "END:VCALENDAR",
  ].filter(Boolean).join("\r\n");
}

function downloadIcs(call: UpcomingCall) {
  const ics = buildIcs(call);
  if (!ics) return;
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `favcy-call-${call.id}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function formatCallTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

const stageColors: Record<string, string> = {
  pipeline: "bg-slate-600",
  onboarding: "bg-blue-600",
  active: "bg-emerald-600",
  exited: "bg-purple-600",
};

const REOPEN_REASONS = [
  { value: "did_not_address", label: "Didn't address my question" },
  { value: "incomplete", label: "Resolution is incomplete" },
  { value: "new_information", label: "I have new information" },
  { value: "other", label: "Other reason" },
];

function formatCurrency(val: number): string {
  if (val >= 10000000) return `₹${(val / 10000000).toFixed(1)}Cr`;
  if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
  if (val >= 1000) return `₹${(val / 1000).toFixed(0)}K`;
  return `₹${val.toLocaleString()}`;
}

function daysUntil(date: string): number {
  return Math.ceil((new Date(date).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

function timeAgo(iso: string | null): string {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

function openKavyaWithPrompt(prompt: string) {
  window.dispatchEvent(new CustomEvent("open-kavya-chat", { detail: { prompt } }));
}

function lastVisitKey(investorId: number) {
  return `investor_last_visit_at_${investorId}`;
}

export default function InvestorPortalDashboard() {
  const [, navigate] = useLocation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [signals, setSignals] = useState<Signals | null>(null);
  const [signalsError, setSignalsError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reopenForTaskId, setReopenForTaskId] = useState<number | null>(null);
  const [reopenReason, setReopenReason] = useState<string>("did_not_address");
  const [reopenNote, setReopenNote] = useState("");
  const [reopenSubmitting, setReopenSubmitting] = useState(false);
  const [reopenError, setReopenError] = useState<string | null>(null);
  const [reopenSuccessFor, setReopenSuccessFor] = useState<{ taskId: number; title: string } | null>(null);
  const [secondaryTotal, setSecondaryTotal] = useState<{ totalApprovedInr: number; allocationCount: number } | null>(null);

  // Capture the previous-visit timestamp ONCE per dashboard mount via a lazy
  // useState initializer (runs once, on the first render). Recording the new
  // visit happens only once on first successful portfolio load (init.recorded
  // flag below). Subsequent refetches (cohort change, post-reopen refresh)
  // must not collapse the window. The previous version overwrote localStorage
  // on every refetch and shrank "since" to seconds after any in-page action.
  // (Architect finding #3, with double-fetch regression also closed.)
  const investorIdRef = useRef<number | null>(null);
  const [previousVisitAt] = useState<string | null>(() => {
    let parsedId: number | null = null;
    try {
      const raw = localStorage.getItem("investor_data");
      if (raw) parsedId = (JSON.parse(raw) as any).id ?? null;
    } catch {
      parsedId = null; // corrupt investor_data — fall back to default window
    }
    investorIdRef.current = parsedId;
    return parsedId ? localStorage.getItem(lastVisitKey(parsedId)) : null;
  });
  const visitRecordedRef = useRef(false);

  const { cohortId } = useActiveCohort();

  const fetchAll = useCallback(async () => {
    const token = localStorage.getItem("investor_token");
    if (!token) { navigate("/investor/login"); return; }

    // Use the captured-once previous visit timestamp from the lazy useState
    // initializer. NEVER setState here — that would put `previousVisitAt`
    // into fetchAll's deps and cause an immediate refetch loop on mount.
    // Default window when we have no prior visit on this device: 7 days.
    const since = previousVisitAt || new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

    try {
      const [portRes, sigRes, secRes] = await Promise.all([
        fetch(withCohort(`${BASE}api/investor-portal/portfolio`, cohortId), { headers: { "x-investor-token": token } }),
        fetch(withCohort(`${BASE}api/investor-portal/dashboard-signals?since=${encodeURIComponent(since)}`, cohortId), { headers: { "x-investor-token": token } }),
        fetch(`${BASE}api/investor-portal/secondary-total`, { headers: { "x-investor-token": token } }),
      ]);
      if (secRes.ok) {
        try { setSecondaryTotal(await secRes.json()); } catch { /* non-fatal */ }
      }
      if (portRes.status === 401) { navigate("/investor/login"); return; }
      if (portRes.ok) {
        setData(await portRes.json());
      } else {
        setError("Unable to load portfolio");
      }
      if (sigRes.ok) {
        setSignals(await sigRes.json());
        setSignalsError(null);
      } else {
        setSignals(null);
        setSignalsError("Could not load activity signals — what changed since your last visit, resolutions to review, and Kavya prompts are temporarily unavailable. Your portfolio data below is up to date.");
      }
      // Record THIS visit only once, after the first successful portfolio load.
      // Re-fetches triggered by user actions must not move the cursor.
      if (!visitRecordedRef.current && investorIdRef.current && portRes.ok) {
        localStorage.setItem(lastVisitKey(investorIdRef.current), new Date().toISOString());
        visitRecordedRef.current = true;
      }
    } catch (err) {
      console.error("Dashboard fetch error:", err);
      setError("Connection error. Please try again.");
    }
    setLoading(false);
    // previousVisitAt is captured once via useState lazy init and never
    // mutates, so including it here keeps the lint rule happy without
    // causing a refetch loop.
  }, [navigate, cohortId, previousVisitAt]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleReopen = async (taskId: number, taskTitle: string) => {
    const token = localStorage.getItem("investor_token");
    if (!token) return;
    setReopenSubmitting(true);
    setReopenError(null);
    try {
      const res = await fetch(`${BASE}api/investor-portal/tasks/${taskId}/reopen`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-investor-token": token },
        body: JSON.stringify({ reason: reopenReason, note: reopenNote.trim() || undefined }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setReopenError(err.error || "Could not reopen this task. Please try again.");
        setReopenSubmitting(false);
        return;
      }
      // Surface a confirmed-success toast independently of the refetch. If
      // the subsequent refetch fails (network blip), the user still knows
      // the reopen itself succeeded server-side. (Architect finding #7.)
      setReopenSuccessFor({ taskId, title: taskTitle });
      setTimeout(() => {
        setReopenSuccessFor(s => (s && s.taskId === taskId ? null : s));
      }, 6000);
      // Optimistically remove from awaiting list and refetch signals.
      setSignals(s => s ? { ...s, awaitingConfirmation: s.awaitingConfirmation.filter(t => t.id !== taskId) } : s);
      setReopenForTaskId(null);
      setReopenNote("");
      setReopenReason("did_not_address");
      // Re-pull signals so counts/prompts reflect the reopen. Note: this
      // call does NOT advance previousVisitAt (see visitInitRef above).
      fetchAll();
    } catch {
      setReopenError("Connection error. Please try again.");
    }
    setReopenSubmitting(false);
  };

  const signalsByCompany = useMemo(() => {
    const m = new Map<number, CompanySignal>();
    if (signals) for (const s of signals.companySignals) m.set(s.companyId, s);
    return m;
  }, [signals]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-[#FF5C00] animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-20">
        <p className="text-white/60">{error || "Unable to load"}</p>
      </div>
    );
  }

  const { investor, totalInvested, activePositions, companies, rofrAlerts } = data as {
    investor: any;
    totalInvested: number;
    activePositions: number;
    companies: PortfolioCompany[];
    rofrAlerts: RofrAlert[];
  };

  const pendingRofrs = rofrAlerts.filter(r => !r.myResponse);
  const isBlindPool = investor.selectProduct === "blind_pool";
  const isPreScreening = investor.selectProduct === "pre_screening";
  const productLabel = isBlindPool ? "Shubh Labh Pool" : isPreScreening ? "Pre-Screening" : null;
  const freeYears = isBlindPool ? 2 : 1;

  const slv = signals?.sinceLastVisit;
  const hasDelta = slv && (slv.newResolutions + slv.newUpdates + slv.newRofrs + slv.unreadNotifications) > 0;
  const showOnboardingBlock = activePositions === 0;
  const awaiting = signals?.awaitingConfirmation || [];
  const dismissed = signals?.recentDismissals || [];
  const upcoming = signals?.upcomingCalls || [];
  const curatedDeals = (signals as any)?.curatedDeals || [];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-3xl font-bold font-serif tracking-tight text-white">
            Welcome back, {investor.name.split(" ")[0]}
          </h1>
          {productLabel && (
            <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full border ${
              isBlindPool
                ? "bg-purple-500/10 border-purple-500/20 text-purple-400"
                : "bg-cyan-500/10 border-cyan-500/20 text-cyan-400"
            }`}>
              <BadgeCheck className="w-3.5 h-3.5" />
              {productLabel} Investor
            </span>
          )}
        </div>
        <p className="text-white/40 text-sm mt-1">
          {previousVisitAt
            ? <>Last visited {timeAgo(previousVisitAt)}</>
            : <>Your Favcy Select control room</>}
        </p>
      </div>

      <ResearchDashboardFeature />

      <InvestorCreditSummary cohortId={cohortId ?? null} />

      {/* Honest degradation: tell the investor when the decision layer is unavailable */}
      {signalsError && (
        <div className="bg-red-500/[0.06] border border-red-500/20 rounded-2xl p-4 flex items-start gap-3">
          <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-red-300 text-sm">{signalsError}</p>
            <button
              onClick={() => fetchAll()}
              className="text-red-300 hover:text-red-200 text-xs underline mt-1"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Since-last-visit banner — the "what changed" layer */}
      {slv && (
        <div className={`rounded-2xl border p-5 ${hasDelta ? "bg-[#FF5C00]/[0.06] border-[#FF5C00]/20" : "bg-white/[0.03] border-white/10"}`}>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className={`text-xs uppercase tracking-wider font-medium ${hasDelta ? "text-[#FF5C00]" : "text-white/40"}`}>
                {previousVisitAt ? "Since you last visited" : "In the last week"}
              </p>
              <p className="text-white text-sm mt-1">
                {hasDelta ? (
                  <>
                    {slv.newResolutions > 0 && <span className="font-medium">{slv.newResolutions} resolution{slv.newResolutions !== 1 ? "s" : ""}</span>}
                    {slv.newResolutions > 0 && (slv.newUpdates + slv.newRofrs + slv.unreadNotifications) > 0 && " · "}
                    {slv.newUpdates > 0 && <span>{slv.newUpdates} new update{slv.newUpdates !== 1 ? "s" : ""}</span>}
                    {slv.newUpdates > 0 && (slv.newRofrs + slv.unreadNotifications) > 0 && " · "}
                    {slv.newRofrs > 0 && <span>{slv.newRofrs} new ROFR</span>}
                    {slv.newRofrs > 0 && slv.unreadNotifications > 0 && " · "}
                    {slv.unreadNotifications > 0 && <span>{slv.unreadNotifications} unread in inbox</span>}
                  </>
                ) : (
                  <span className="text-white/50">All caught up — nothing new since you were here.</span>
                )}
              </p>
            </div>
            {hasDelta && (
              <button
                onClick={() => navigate("/investor/portal/inbox")}
                title="Opens the full notifications inbox"
                className="text-[#FF5C00] hover:text-[#FF7A1F] text-xs font-medium flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[#FF5C00]/30 hover:border-[#FF5C00]/50"
              >
                Open notifications <ArrowRight className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* ROFR alerts (kept — they're action-required) */}
      {pendingRofrs.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Bell className="w-4 h-4 text-amber-400" />
            <p className="text-amber-400 text-sm font-medium">ROFR Alert — Action Required</p>
          </div>
          <div className="space-y-2">
            {pendingRofrs.map(rofr => (
              <div key={rofr.id} className="flex items-center justify-between bg-amber-500/5 rounded-lg p-3">
                <div>
                  <p className="text-white text-sm font-medium">{rofr.companyName} — {rofr.roundName}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-white/40">
                    {rofr.roundSize && <span>Size: {rofr.roundSize}</span>}
                    {rofr.valuation && <span>Valuation: {rofr.valuation}</span>}
                    {rofr.deadline && (
                      <span className={`flex items-center gap-1 ${daysUntil(rofr.deadline) <= 7 ? "text-red-400" : "text-amber-400"}`}>
                        <Clock className="w-3 h-3" />
                        {daysUntil(rofr.deadline)} days left
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => navigate("/investor/portal/rofr")}
                  className="text-amber-400 hover:text-amber-300 text-xs font-medium flex items-center gap-1"
                >
                  Respond <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Success toast — confirms the reopen succeeded server-side, even if
          the subsequent refetch fails. Auto-dismisses after 6s. */}
      {reopenSuccessFor && (
        <div
          role="status"
          aria-live="polite"
          className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 flex items-start justify-between gap-3"
        >
          <div className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-emerald-300 text-sm font-medium">Reopened — your advisor has been notified.</p>
              <p className="text-white/60 text-xs mt-0.5">"{reopenSuccessFor.title}"</p>
            </div>
          </div>
          <button
            onClick={() => setReopenSuccessFor(null)}
            className="text-white/40 hover:text-white text-xs px-2"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* Curated for you — advisor-originated deals reviewed by Favcy
          (PRD curated-deals-v1 § 8). Sits above Upcoming calls because a
          time-bound deal is more perishable than a scheduled call. The band
          only renders when there's at least one active item that hasn't been
          declined; empty state stays silent so we don't demote the surface. */}
      {curatedDeals.length > 0 && (
        <div className="bg-gradient-to-br from-emerald-500/[0.07] to-[#FF5C00]/[0.05] border border-emerald-500/20 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-emerald-300" />
              <h2 className="text-base font-serif font-bold text-white">
                Curated for you
                {curatedDeals.filter((d: any) => d.myState === "distributed").length > 0 && (
                  <span className="ml-2 text-xs font-normal text-emerald-300">
                    ({curatedDeals.filter((d: any) => d.myState === "distributed").length} new)
                  </span>
                )}
              </h2>
            </div>
            <button
              onClick={() => navigate("/investor/portal/curated")}
              className="text-emerald-300 hover:text-emerald-200 text-xs font-medium flex items-center gap-1"
            >
              See all <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <p className="text-white/50 text-xs mb-4">
            Hand-picked opportunities your advisor sourced. Each one reviewed by Favcy before it reached you.
          </p>
          <div className="space-y-2">
            {curatedDeals.slice(0, 3).map((d: any) => {
              const isNew = d.myState === "distributed";
              const inrShort = (n: any) => {
                if (n == null) return "—"; const x = Number(n);
                if (x >= 10000000) return `₹${(x / 10000000).toFixed(1)}Cr`;
                if (x >= 100000) return `₹${(x / 100000).toFixed(1)}L`;
                return `₹${x.toLocaleString("en-IN")}`;
              };
              return (
                <button
                  key={d.id}
                  onClick={() => navigate(`/investor/portal/curated/${d.slug}`)}
                  className="w-full text-left bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 hover:border-emerald-500/30 rounded-xl p-3 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {isNew && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />}
                        <p className="text-white text-sm font-medium truncate">{d.title}</p>
                        <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                          d.dealType === "primary"
                            ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                            : "bg-blue-500/15 text-blue-300 border-blue-500/30"
                        }`}>{d.dealType}</span>
                        {d.status === "paused" && (
                          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border bg-amber-500/15 text-amber-300 border-amber-500/30">paused</span>
                        )}
                      </div>
                      <p className="text-white/50 text-xs mt-1">
                        Curated by {d.advisor?.name || "your advisor"} · Min ticket {inrShort(d.minTicketInr)} · Raise {inrShort(d.raiseAmountInr)}
                      </p>
                    </div>
                    <span className="text-emerald-300 text-xs flex items-center gap-1 flex-shrink-0">
                      View <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Upcoming calls — execution surface (T004). Above resolutions because
          a call you haven't taken is more time-sensitive than a closure you
          haven't reviewed. Each row is a real meeting your advisor scheduled,
          with the join link and an .ics download for your calendar app. */}
      {upcoming.length > 0 && (
        <div className="bg-blue-500/[0.05] border border-blue-500/20 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-blue-300" />
              <h2 className="text-base font-serif font-bold text-white">Upcoming calls</h2>
              <span className="text-[10px] uppercase tracking-wider text-white/40 font-medium px-2 py-0.5 rounded-full bg-white/5 border border-white/10">
                scheduled by your advisor
              </span>
            </div>
          </div>
          <div className="space-y-3">
            {upcoming.map(c => c.scheduledFor && (
              <div key={c.id} className="bg-black/20 border border-white/5 rounded-xl p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {c.companyName && (
                        <span className="text-[10px] uppercase tracking-wider text-[#FF5C00] font-medium">
                          {c.companyName}
                        </span>
                      )}
                      <span className="text-[10px] text-blue-300 font-mono">
                        {formatCallTime(c.scheduledFor)}
                      </span>
                    </div>
                    <p className="text-white text-sm font-medium mt-1">{c.title}</p>
                    {c.meetingNote && (
                      <p className="text-white/60 text-xs mt-2 leading-relaxed italic border-l-2 border-blue-500/30 pl-3">
                        {c.meetingNote}
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  {c.meetingLink && (
                    <a
                      href={c.meetingLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-300 hover:text-blue-200 text-xs font-medium flex items-center gap-1.5"
                    >
                      <ExternalLink className="w-3 h-3" /> Join call
                    </a>
                  )}
                  <button
                    onClick={() => downloadIcs(c)}
                    className="text-white/60 hover:text-white text-xs flex items-center gap-1.5"
                    title="Download .ics file to add to your calendar"
                  >
                    <CalendarPlus className="w-3 h-3" /> Add to calendar
                  </button>
                  {c.companyName && (
                    <button
                      onClick={() => openKavyaWithPrompt(`What should I prepare to ask on the upcoming call about ${c.companyName} — "${c.title}"?`)}
                      className="text-white/40 hover:text-white/70 text-xs flex items-center gap-1.5"
                    >
                      <MessageCircle className="w-3 h-3" /> Prep with Kavya
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Awaiting your confirmation — closure visibility, the reverse-loop made primary */}
      {awaiting.length > 0 && (
        <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <h2 className="text-base font-serif font-bold text-white">Resolutions to review</h2>
              <span className="text-[10px] uppercase tracking-wider text-white/40 font-medium px-2 py-0.5 rounded-full bg-white/5 border border-white/10">
                last 30 days
              </span>
            </div>
            <button
              onClick={() => navigate("/investor/portal/inbox")}
              className="text-white/40 hover:text-white text-xs flex items-center gap-1"
            >
              See all <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <div className="space-y-3">
            {awaiting.map(t => (
              <div key={t.id} className="bg-black/20 border border-white/5 rounded-xl p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {t.companyName && (
                        <span className="text-[10px] uppercase tracking-wider text-[#FF5C00] font-medium">
                          {t.companyName}
                        </span>
                      )}
                      <span className="text-[10px] text-white/30">resolved {timeAgo(t.closedAt)}</span>
                      {t.reopenCount && t.reopenCount > 0 ? (
                        <span className="text-[10px] text-amber-400 flex items-center gap-1">
                          <RotateCcw className="w-2.5 h-2.5" />
                          re-resolved (round {t.reopenCount + 1})
                        </span>
                      ) : null}
                    </div>
                    <p className="text-white text-sm font-medium mt-1">{t.title}</p>
                    {t.resolutionNote && (
                      <p className="text-white/60 text-xs mt-2 leading-relaxed italic border-l-2 border-emerald-500/30 pl-3">
                        "{t.resolutionNote}"
                      </p>
                    )}
                  </div>
                </div>
                {reopenForTaskId === t.id ? (
                  <div className="mt-4 pt-4 border-t border-white/10 space-y-3">
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-white/40 font-medium">
                        Why are you reopening?
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                        {REOPEN_REASONS.map(r => (
                          <label
                            key={r.value}
                            className={`flex items-center gap-2 text-xs p-2 rounded border cursor-pointer ${
                              reopenReason === r.value
                                ? "bg-[#FF5C00]/10 border-[#FF5C00]/30 text-white"
                                : "bg-white/[0.02] border-white/10 text-white/60 hover:border-white/20"
                            }`}
                          >
                            <input
                              type="radio"
                              name={`reopen-reason-${t.id}`}
                              value={r.value}
                              checked={reopenReason === r.value}
                              onChange={() => setReopenReason(r.value)}
                              className="sr-only"
                            />
                            {r.label}
                          </label>
                        ))}
                      </div>
                    </div>
                    <textarea
                      value={reopenNote}
                      onChange={e => setReopenNote(e.target.value.slice(0, 500))}
                      placeholder="Add context for your advisor (optional, max 500 chars)"
                      className="w-full bg-black/30 border border-white/10 rounded-lg p-2.5 text-xs text-white placeholder-white/30 focus:outline-none focus:border-[#FF5C00]/40"
                      rows={2}
                    />
                    {reopenError && <p className="text-red-400 text-xs">{reopenError}</p>}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleReopen(t.id, t.title)}
                        disabled={reopenSubmitting}
                        className="bg-[#FF5C00] text-white text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-[#FF7A1F] disabled:opacity-50 flex items-center gap-1.5"
                      >
                        {reopenSubmitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                        Reopen task
                      </button>
                      <button
                        onClick={() => { setReopenForTaskId(null); setReopenNote(""); setReopenError(null); }}
                        className="text-white/50 hover:text-white text-xs px-2 py-1.5"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 flex items-center gap-3">
                    <button
                      onClick={() => { setReopenForTaskId(t.id); setReopenError(null); }}
                      className="text-amber-400 hover:text-amber-300 text-xs font-medium flex items-center gap-1.5"
                    >
                      <RotateCcw className="w-3 h-3" /> This didn't address it — reopen
                    </button>
                    {t.companyName && (
                      <button
                        onClick={() => openKavyaWithPrompt(`Walk me through your advisor's resolution on ${t.companyName} for: "${t.title}"`)}
                        className="text-white/40 hover:text-white/70 text-xs flex items-center gap-1.5"
                      >
                        <MessageCircle className="w-3 h-3" /> Ask Kavya about this
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Marked as not actionable — closes the loop on dismissed questions.
          Without this, an investor's question can vanish silently after the
          advisor dismisses it. (SHIPPING_CHECKLIST Part 4 known issue + I3.) */}
      {dismissed.length > 0 && (
        <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-amber-400" />
              <h2 className="text-base font-serif font-bold text-white">Marked as not actionable</h2>
              <span className="text-[10px] uppercase tracking-wider text-white/40 font-medium px-2 py-0.5 rounded-full bg-white/5 border border-white/10">
                last 30 days
              </span>
            </div>
          </div>
          <p className="text-white/50 text-xs mb-3">
            Your advisor decided these questions don't need follow-up work. If you disagree, ask Kavya for context or raise a fresh task.
          </p>
          <div className="space-y-3">
            {dismissed.map(t => (
              <div key={t.id} className="bg-black/20 border border-white/5 rounded-xl p-4">
                <div className="flex items-center gap-2 flex-wrap">
                  {t.companyName && (
                    <span className="text-[10px] uppercase tracking-wider text-[#FF5C00] font-medium">
                      {t.companyName}
                    </span>
                  )}
                  <span className="text-[10px] text-white/30">dismissed {timeAgo(t.dismissedAt)}</span>
                </div>
                <p className="text-white text-sm font-medium mt-1">{t.title}</p>
                {t.dismissReason ? (
                  <p className="text-white/60 text-xs mt-2 leading-relaxed italic border-l-2 border-amber-500/30 pl-3">
                    "{t.dismissReason}"
                  </p>
                ) : (
                  <p className="text-white/40 text-xs mt-2 italic">No reason was provided by your advisor.</p>
                )}
                <div className="mt-3">
                  <button
                    onClick={() => openKavyaWithPrompt(
                      t.companyName
                        ? `My question on ${t.companyName} — "${t.title}" — was marked not actionable${t.dismissReason ? ` because: "${t.dismissReason}"` : ""}. Help me understand whether to push back or rephrase it.`
                        : `My question — "${t.title}" — was marked not actionable${t.dismissReason ? ` because: "${t.dismissReason}"` : ""}. Help me understand whether to push back or rephrase it.`
                    )}
                    className="text-[#FF5C00] hover:text-[#FF7A1F] text-xs font-medium flex items-center gap-1.5"
                  >
                    <MessageCircle className="w-3 h-3" /> Ask Kavya about this
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Ask Kavya — promoted from sidebar to a primary surface */}
      {signals && signals.kavyaPrompts.length > 0 && (
        <div className="bg-gradient-to-br from-[#FF5C00]/[0.08] to-[#FF5C00]/[0.02] border border-[#FF5C00]/20 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-[#FF5C00]" />
            <h2 className="text-base font-serif font-bold text-white">Ask Kavya about your portfolio</h2>
          </div>
          <p className="text-white/50 text-xs mb-4">Suggested questions based on what's actually happening:</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {signals.kavyaPrompts.map((p, i) => (
              <button
                key={i}
                onClick={() => openKavyaWithPrompt(p.prompt)}
                className="text-left bg-black/20 hover:bg-black/30 border border-white/10 hover:border-[#FF5C00]/30 rounded-lg p-3 transition-all group"
              >
                <p className="text-[10px] uppercase tracking-wider text-[#FF5C00] font-medium mb-1.5">{p.label}</p>
                <p className="text-white/80 text-xs leading-relaxed group-hover:text-white">{p.prompt}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Snapshot stats — kept but compact, no longer the centerpiece */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white/[0.03] border border-white/10 rounded-xl p-5">
          <div className="flex items-center gap-2 text-white/40 mb-2">
            <IndianRupee className="w-4 h-4" />
            <p className="text-xs uppercase tracking-wider font-medium">Total Invested</p>
          </div>
          <p className="text-2xl font-bold text-[#FF5C00] font-mono">
            {totalInvested > 0 ? formatCurrency(totalInvested) : "—"}
          </p>
        </div>

        <div className="bg-white/[0.03] border border-white/10 rounded-xl p-5" data-testid="card-secondary-commitment">
          <div className="flex items-center gap-2 text-white/40 mb-2">
            <IndianRupee className="w-4 h-4" />
            <p className="text-xs uppercase tracking-wider font-medium">Secondary Commitment</p>
          </div>
          <p className="text-2xl font-bold text-[#FF5C00] font-mono">
            {secondaryTotal && secondaryTotal.totalApprovedInr > 0 ? formatCurrency(secondaryTotal.totalApprovedInr) : "—"}
          </p>
          <p className="text-[10px] text-white/30 mt-1">
            {secondaryTotal && secondaryTotal.allocationCount > 0
              ? `${secondaryTotal.allocationCount} approved ${secondaryTotal.allocationCount === 1 ? "basket" : "baskets"}`
              : "no approved baskets yet"}
          </p>
        </div>

        <div className="bg-white/[0.03] border border-white/10 rounded-xl p-5">
          <div className="flex items-center gap-2 text-white/40 mb-2">
            <Building2 className="w-4 h-4" />
            <p className="text-xs uppercase tracking-wider font-medium">Active Positions</p>
          </div>
          <p className="text-2xl font-bold text-white font-mono">{activePositions}</p>
          <p className="text-[10px] text-white/30 mt-1">companies in portfolio</p>
        </div>

        <button
          onClick={() => navigate("/investor/portal/inbox")}
          className="bg-white/[0.03] border border-white/10 rounded-xl p-5 hover:bg-white/[0.05] hover:border-white/20 transition-all text-left"
        >
          <div className="flex items-center gap-2 text-white/40 mb-2">
            <Inbox className="w-4 h-4" />
            <p className="text-xs uppercase tracking-wider font-medium">Inbox</p>
          </div>
          <p className="text-2xl font-bold text-white font-mono">{slv?.unreadNotifications ?? 0}</p>
          <p className="text-[10px] text-white/30 mt-1">unread</p>
        </button>
      </div>

      {/* Portfolio companies — list with real signals (not a directory) */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-serif font-bold text-white">Your Portfolio Companies</h2>
          <p className="text-white/30 text-[10px]">Last update · open tasks</p>
        </div>
        {companies.length === 0 ? (
          <div className="bg-white/[0.03] border border-white/10 rounded-xl p-12 text-center">
            <Building2 className="w-8 h-8 text-white/20 mx-auto mb-3" />
            <p className="text-white/40">No companies allocated yet</p>
            <p className="text-white/20 text-sm mt-1">Contact Favcy to set up your portfolio</p>
          </div>
        ) : (
          <div className="space-y-3">
            {companies.map(({ company, amountInvested, latestSignal }) => {
              const sig = signalsByCompany.get(company.id);
              const dsu = sig?.daysSinceLastUpdate;
              const updateLabel = dsu === null || dsu === undefined
                ? "no updates yet"
                : dsu === 0 ? "updated today"
                : dsu === 1 ? "updated yesterday"
                : `updated ${dsu}d ago`;
              const updateTone =
                dsu === null || dsu === undefined ? "text-white/30"
                : dsu <= 7 ? "text-emerald-400/80"
                : dsu <= 21 ? "text-amber-400/80"
                : "text-red-400/80";
              const openCount = sig?.openTasksMine ?? 0;

              return (
                <button
                  key={company.id}
                  onClick={() => navigate(`/investor/portal/company/${company.id}#activity`)}
                  className="w-full bg-white/[0.03] border border-white/10 rounded-xl p-5 hover:bg-white/[0.05] hover:border-white/20 transition-all group text-left"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4 min-w-0 flex-1">
                      {company.logoUrl ? (
                        <img src={company.logoUrl} alt="" className="w-12 h-12 rounded-lg object-cover border border-white/10 flex-shrink-0" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#FF5C00] to-[#E55200] flex items-center justify-center text-white font-bold font-serif text-lg flex-shrink-0">
                          {company.name.charAt(0)}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <h3 className="text-white font-medium text-sm truncate">{company.name}</h3>
                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                          {company.sector && (
                            <span className="text-white/40 text-xs flex items-center gap-1">
                              <Target className="w-3 h-3" />{company.sector}
                            </span>
                          )}
                          {company.location && (
                            <span className="text-white/30 text-xs flex items-center gap-1">
                              <MapPin className="w-3 h-3" />{company.location}
                            </span>
                          )}
                        </div>
                        {latestSignal ? (
                          <div className="mt-2 flex items-center gap-1.5 text-xs text-white/60 truncate">
                            <span className="flex-shrink-0">{SIGNAL_ICONS[latestSignal.kind]}</span>
                            <span className="truncate">{latestSignal.title}</span>
                            <span className="text-white/30 flex-shrink-0">· {formatSignalAge(latestSignal.occurredAt)}</span>
                          </div>
                        ) : (
                          <div className="mt-2 text-xs text-white/25">No recent activity</div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-5 flex-shrink-0">
                      <div className="text-right hidden sm:block">
                        <p className={`text-xs ${updateTone}`}>{updateLabel}</p>
                        {openCount > 0 ? (
                          <p className="text-amber-400 text-[10px] mt-1 flex items-center gap-1 justify-end">
                            <AlertCircle className="w-2.5 h-2.5" />
                            {openCount} open task{openCount !== 1 ? "s" : ""}
                          </p>
                        ) : sig?.lastBankerActionAt ? (
                          <p className="text-white/30 text-[10px] mt-1">
                            advisor action {timeAgo(sig.lastBankerActionAt)}
                          </p>
                        ) : (
                          <p className="text-white/20 text-[10px] mt-1">no advisor activity</p>
                        )}
                      </div>
                      {amountInvested && (
                        <div className="text-right">
                          <p className="text-[#FF5C00] font-mono font-medium text-sm">{formatCurrency(amountInvested)}</p>
                          <p className="text-white/20 text-[10px]">invested</p>
                        </div>
                      )}
                      <ChevronRight className="w-4 h-4 text-white/20 group-hover:text-[#FF5C00] transition-colors" />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* "How Favcy Select Works" — onboarding only. Hide for active investors. */}
      {showOnboardingBlock && (
        <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
          <h2 className="text-lg font-serif font-bold text-white mb-1">How Favcy Select Works</h2>
          <p className="text-white/30 text-xs mb-5">Your investment journey across three stages</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-full bg-emerald-500/15 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <Search className="w-4 h-4 text-emerald-400" />
                </div>
                <div>
                  <p className="text-emerald-400 text-[10px] uppercase tracking-wider font-medium">Stage 1</p>
                  <p className="text-white font-medium text-sm">Origination</p>
                </div>
              </div>
              <p className="text-white/40 text-xs leading-relaxed pl-12">
                Deal sourcing, due diligence, and investment structuring — completed for your portfolio.
              </p>
            </div>
            <div className="relative">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-full bg-blue-500/15 border border-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <Settings2 className="w-4 h-4 text-blue-400" />
                </div>
                <div>
                  <p className="text-blue-400 text-[10px] uppercase tracking-wider font-medium">Stage 2</p>
                  <p className="text-white font-medium text-sm">Management</p>
                </div>
              </div>
              <p className="text-white/40 text-xs leading-relaxed pl-12">
                Portfolio monitoring and venture support via the <span className="text-white/60">Favcy Select platform</span>. <span className="text-white/60">Free for {freeYears === 2 ? "the first 2 years" : "Year 1"}</span>, then ₹14,999/year thereafter.
              </p>
            </div>
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-full bg-[#FF5C00]/15 border border-[#FF5C00]/20 flex items-center justify-center flex-shrink-0">
                  <ExitIcon className="w-4 h-4 text-[#FF5C00]" />
                </div>
                <div>
                  <p className="text-[#FF5C00] text-[10px] uppercase tracking-wider font-medium">Stage 3</p>
                  <p className="text-white font-medium text-sm">Exit</p>
                </div>
              </div>
              <p className="text-white/40 text-xs leading-relaxed pl-12">
                After 3 years, leverage Favcy Advisors — our in-house investment banking arm — for exits at a <span className="text-white/60">5% success fee</span>.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* "Invest more in Favcy Select" — promoted from a footer link to a
          first-class card. The previous treatment was so quiet investors
          weren't finding it. Sits at the bottom of the dashboard so it's not
          the loudest CTA, but it's now visible and clickable on its own. */}
      <div className="bg-gradient-to-br from-[#FF5C00]/[0.08] to-purple-500/[0.05] border border-[#FF5C00]/25 rounded-2xl p-5 md:p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="w-11 h-11 rounded-xl bg-[#FF5C00]/15 border border-[#FF5C00]/25 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-[#FF5C00]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium text-sm md:text-base">Invest more in Favcy Select</p>
              <p className="text-white/55 text-xs md:text-sm mt-1 leading-relaxed">
                Increase your allocation, top up an existing cohort, or join an upcoming one. Your advisor will reach out with available room and next steps.
              </p>
            </div>
          </div>
          <button
            onClick={() => window.open("mailto:Kavya@favcyselect.com?subject=Interest%20in%20Favcy%20Select%20—%20Invest%20More", "_blank")}
            className="bg-[#FF5C00] hover:bg-[#FF7A1F] text-white text-sm font-medium px-4 py-2.5 rounded-lg flex items-center gap-2 transition-colors flex-shrink-0"
          >
            Talk to your advisor <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <InvestorPortalDisclaimer />
    </div>
  );
}
